import { z } from 'zod'

import { AssignableRole, CONDITION_STATUS, ConfigType, TaskCategory, taskStateValues, TaskType } from './tasks'

const TaskUserSchema = z.object({
  id: z.string(),
  name: z.string(),
})

const TaskSystemSchema = z.object({
  createdBy: TaskUserSchema,
  updatedBy: TaskUserSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

const TaskTimestampSchema = z.object({
  startDate: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  dueDate: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  completedAt: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
})

const TaskDefinitionAttributesSchema = z.object({
  id: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  name: z.string(),
  taskModuleID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  role: z.nativeEnum(AssignableRole),
  description: z.string(),
  i18nTerm: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  deepLinkType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  deepLinkTarget: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  isRepeatable: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  category: z
    .nativeEnum(TaskCategory)
    .nullish()
    .transform((data) => data ?? undefined),
  type: z
    .nativeEnum(TaskType)
    .nullish()
    .transform((data) => data ?? undefined),
})

const TaskAssigneeSchema = z.object({
  userID: z.string(),
  fullName: z.string(),
  email: z.string(),
  initials: z.string(),
  role: z.nativeEnum(AssignableRole),
})

const ConditionConfigurationSchema = z.object({
  type: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  configType: z
    .nativeEnum(ConfigType)
    .nullish()
    .transform((data) => data ?? undefined),
  selectorFieldPath: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  secondaryField: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  multiple: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  eventType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  value: z
    .unknown()
    .nullish()
    .transform((data) => data ?? undefined),
  referenceLabel: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  conditionStatus: z
    .nativeEnum(CONDITION_STATUS)
    .nullish()
    .transform((data) => data ?? undefined),
})

const TaskSchema = z.object({
  id: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  notes: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  taskDefinitionAttributes: TaskDefinitionAttributesSchema,
  timestamps: TaskTimestampSchema,
  state: z.enum(taskStateValues),
  sequenceNumber: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  assignee: TaskAssigneeSchema.nullish().transform((data) => data ?? undefined),
  lastTouchedByUser: TaskUserSchema.nullish().transform((data) => data ?? undefined),
  isIrrelevant: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  isManualDueDate: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  system: TaskSystemSchema.nullish().transform((data) => data ?? undefined),
  filterConditions: z
    .array(ConditionConfigurationSchema.omit({ referenceLabel: true }))
    .nullish()
    .transform((data) => data ?? undefined),
  startConditions: z
    .array(ConditionConfigurationSchema)
    .nullish()
    .transform((data) => data ?? undefined),
  dueConditions: z
    .array(ConditionConfigurationSchema)
    .nullish()
    .transform((data) => data ?? undefined),
  completionConditions: z
    .array(ConditionConfigurationSchema)
    .nullish()
    .transform((data) => data ?? undefined),
  isDueDateChangeable: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
})

export const RequiredActionTaskSchema = TaskSchema.extend({
  entityID: z.string(),
  entityType: z.enum(['booking', 'shipment']),
})

export const PaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  totalCount: z.number(),
  totalPages: z.number(),
})

export const StatsSchema = z.object({
  totalDueNextWeekCount: z.number(),
  totalOverDueCount: z.number(),
})

export const CustomerRequiredActionSchema = z.object({
  customerID: z.string(),
  tasks: z.array(RequiredActionTaskSchema),
  pagination: PaginationSchema,
  stats: StatsSchema,
})

export const ShipmentRequiredActionSchema = z.object({
  id: z.string(),
  entityID: z.string(),
  entityType: z.enum(['booking', 'shipment']),
  tasks: z.array(TaskSchema),
})

export const RequiredActionsFilterLookupSchema = z.object({
  shippers: z.array(z.string()),
  consignees: z.array(z.string()),
  subscriptions: z.array(z.string()),
})

export type RequiredActionTask = z.infer<typeof RequiredActionTaskSchema>
export type CustomerRequiredAction = z.infer<typeof CustomerRequiredActionSchema>
export type ShipmentRequiredAction = z.infer<typeof ShipmentRequiredActionSchema>
export type RequiredActionsFilterLookup = z.infer<typeof RequiredActionsFilterLookupSchema>
